import { Container, Paper, Button, Box } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import PictureInput from 'Components/Input/PictureInput';
import LayoutWithPageLoading from 'Components/layout/LayoutWithPageLoading';
import TagsListSelectionForm from 'Features/Tags/TagsListSelectionForm';
import { Formik } from 'formik';
import useAsyncTask from 'hooks/useAsyncTask';
import { IReactFormProps, MLFormContent } from 'react-forms';
import { OWeeklyTech } from 'Models/WeeklyTech';
import { WeeklyTech, WeeklyTechState } from 'Models/WeeklyTech/@types';
import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { ReduxStore } from 'stores';
import { weeklyTechFormToRequestDataMapping } from '../../Models/WeeklyTech/weeklyTechParser';
import Loader from 'Components/Loader';
import useToastMessage from 'hooks/useToastMessage';
import { TDispatch } from 'Typings/Global';
import HeaderActionButtons from 'Components/Buttons/HeaderActionButtons';
import useConfirmationDialog from 'hooks/useConfirmationDialog';
import { Editor } from '@tinymce/tinymce-react';
import KeywordsForm from 'Components/KeywordsForm';
import EditorDetails from 'Components/Editor';
import { UserState } from 'Models/Users/@types';
import { TINY_API_KEY } from 'Resources/Config';

type StoreSelection = Pick<WeeklyTechState, 'weeklyTechPostData'> & Pick<UserState, 'appUser'>;

export interface WeeklyTechDetailsProps extends RouteComponentProps<{ id: string }> { }

const WeeklyTechDetails: FC<WeeklyTechDetailsProps> = ({ match, history }) => {
    const classes = useStyles();
    const id = match.params['id'];

    const dispatch = useDispatch<TDispatch>();
    const { weeklyTechPostData, appUser } = useSelector<ReduxStore, StoreSelection>(({ WeeklyTech: { weeklyTechPostData }, Users: { appUser } }) => ({ weeklyTechPostData, appUser }));
    const getDataTask = useAsyncTask(async () => dispatch(OWeeklyTech.getItem(id, { filter: { include: ['tags'] } })));
    const togglePublishTask = useAsyncTask(async () => await dispatch(OWeeklyTech.postActivity(OWeeklyTech.Activities.TOGGLE_PUBLISH, {}, { id: weeklyTechPostData?.id, publish: !weeklyTechPostData?.isPublished })))
    const deleteTask = useAsyncTask(async () => await dispatch(OWeeklyTech.deleteItem(weeklyTechPostData!.id, {}, undefined, { weeklyTechPostData })));

    const updateTask = useAsyncTask(async (formData: Partial<WeeklyTech> & { id: string }) => {
        return await dispatch(OWeeklyTech.patchItem(formData.id, formData))
    });

    const withToast = useToastMessage();
    const withConfirmationDialog = useConfirmationDialog();

    useEffect(() => {
        getDataTask.run();
        // eslint-disable-next-line
    }, []);

    const handleSubmit = (formData: Partial<WeeklyTech>) => {

        let completeFormData: Partial<WeeklyTech> = { ...formData, tagIds: formData?.tags?.map(x => x.id), authorId: appUser?.id };
        const temp = document.createElement('div');
        temp.innerHTML = completeFormData.body || '';
        const hTags = ["h1", "h2", "h3", "h4", "h5", "h6"];
        const elements = temp.querySelectorAll<HTMLElement>(hTags.join());
        elements.forEach(el => {
            const text = el.innerText;
            const id = text.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)?.map(x => x.toLowerCase()).join('-')!;
            el.setAttribute("id", id);
            const anchor = document.createElement('a');
            anchor.href = `#${id}`;
            anchor.innerText = text;
            el.innerHTML = "";
            el.appendChild(anchor);
        });

        completeFormData.body = temp.innerHTML;

        completeFormData = weeklyTechFormToRequestDataMapping(completeFormData);

        temp.remove();

        withToast(async () => {
            if (completeFormData.id) {
                const res = await updateTask.run({ id: completeFormData.id, ...completeFormData });
                res && history.replace(`/dashboard/weekly-tech/${res.data.slug}`);
            }
        })
    }
    const togglePublish = () => {
        if (weeklyTechPostData?.id) {
            withToast(togglePublishTask.run, { successToastMessage: weeklyTechPostData.isPublished ? "Unpublished successfully" : "Published successfully" });
        }
    }
    const deleteWeeklyPost = () => {
        withConfirmationDialog(async () => {
            await deleteTask.run();
            history.replace('/dashboard/weekly-tech');
        }, { agreeText: 'Delete', cancelText: 'Cancel', message: 'Do you want to delete this Post?' }, { successToastMessage: "Delete Success" })
    }

    const FormConfig: IReactFormProps['config'] = [
        {
            type: "text",
            valueKey: 'shortDescription',
            fieldProps: {
                fullWidth: true,
                multiline: true,
                label: "Short Description"
            }
        }
    ]

    const updating = updateTask.status === "PROCESSING";
    return (
        <LayoutWithPageLoading loading={getDataTask.status === 'PROCESSING'} assertedItems={[weeklyTechPostData]} >
            <Formik<Partial<WeeklyTech>>
                initialValues={weeklyTechPostData || {}}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {
                    formikProps => (
                        <Container className={classes.container}>
                            <Box className={classes.fixedButtonContainer} >
                                <HeaderActionButtons items={[
                                    { icon: weeklyTechPostData?.isPublished ? 'lock_open' : 'lock', onClick: togglePublish },
                                    { icon: 'delete', onClick: deleteWeeklyPost }
                                ]} />
                                <Button disabled={updating} type="submit" onSubmit={formikProps.submitForm} onClick={formikProps.submitForm} color={'primary'} variant={'contained'} size={'large'} >
                                    {updating ? <Loader size={'xs'} /> : 'SAVE'}
                                </Button>
                            </Box>
                            <div className={clsx(classes.metaContainer)} >
                                <Paper className={clsx(classes.formContainer, classes.pictureInputContainer)} >
                                    <MLFormContent
                                        formId={'meta-form'}
                                        formikProps={formikProps}
                                        settings={{ verticalSpacing: 16 }}
                                        schema={[
                                            {
                                                type: 'text',
                                                valueKey: 'title',
                                                fieldProps: {
                                                    fullWidth: true,
                                                    label: 'Title',
                                                    multiline: true
                                                }
                                            },
                                            {
                                                type: 'text',
                                                valueKey: 'slug',
                                                fieldProps: {
                                                    fullWidth: true,
                                                    label: 'Slug'
                                                }
                                            },
                                            {
                                                type: 'text',
                                                valueKey: 'ogTitle',
                                                fieldProps: {
                                                    fullWidth: true,
                                                    label: 'OG Title',
                                                    multiline: true
                                                }
                                            },
                                        ]}
                                    />
                                    <PictureInput
                                        withCropping
                                        cropConfig={{ aspect: 16 / 9 }}
                                        initialPicture={weeklyTechPostData?.coverPicture}
                                        onPicUpload={coverPicture => formikProps.setFieldValue('coverPicture', coverPicture)}
                                        onPictureRemove={() => formikProps.setFieldValue("coverPicture", undefined)}
                                    />
                                    <Box my={2} >
                                        <KeywordsForm
                                            initialKeywords={formikProps.values['keywords']}
                                            textFieldProps={{ label: "Keywords", fullWidth: true }}
                                            chipProps={{ size: 'small' }}
                                            onKeywordsUpdate={keywords => formikProps.setFieldValue('keywords', keywords)}
                                        />
                                    </Box>
                                    <TagsListSelectionForm initialTags={weeklyTechPostData?.tags || []} onChange={tags => formikProps.setFieldValue('tags', tags)} />
                                </Paper>
                            </div>
                            <Box flex={1} >
                                <Paper className={clsx(classes.formContainer)} >
                                    <Editor
                                        apiKey={TINY_API_KEY}
                                        init={{
                                            minHeight: '50px !important',
                                            toolbar: 'bold link italic code undo redo',
                                            plugins: ['link']
                                        }}
                                        initialValue={weeklyTechPostData?.description || ''}
                                        onChange={e => formikProps.setFieldValue('description', e.target.getContent())}
                                        inline
                                    />
                                </Paper>
                                <Paper className={clsx(classes.contentContainer, classes.formContainer)} >
                                    <MLFormContent
                                        formId={'content-form'}
                                        formikProps={formikProps}
                                        settings={{ verticalSpacing: 16 }}
                                        schema={FormConfig}
                                    />
                                </Paper>
                                <EditorDetails
                                    initialValue={weeklyTechPostData?.body}
                                    onChangeValue={(e: any) => formikProps.setFieldValue('body', e.target.getContent())}
                                />
                            </Box>
                        </Container>
                    )
                }
            </Formik>
        </LayoutWithPageLoading>
    );
}
const useStyles = makeStyles<Theme>((theme) => {
    return (createStyles({
        container: {
            display: 'flex',
            padding: '88px 0px'
        },
        metaContainer: {
            width: 320,
            marginRight: 16,
        },
        formContainer: {
            padding: 24,
            borderRadius: 4,
            marginBottom: 16
        },
        contentContainer: {
            flex: 1
        },
        pictureInputContainer: {
            padding: 16,
        },
        fixedButtonContainer: {
            display: 'flex',
            alignItems: 'center',
            position: 'fixed',
            top: 64 + 24,
            right: 24
        }
    }))
})
export default WeeklyTechDetails;  